import { Controller } from "@hotwired/stimulus"
import * as Turbo from "@hotwired/turbo"
import { safeStorageGet, safeStorageRemove, safeStorageSet } from "./safe_storage"

export function normalizeStoredSearch(search) {
  if (search == null || search === "") return ""
  return search.startsWith("?") ? search : `?${search}`
}

export function createAdminIndexFiltersStorage(config) {
  const {
    storageKey,
    restoreInFlightKey,
    isIndexPath,
    mergeQueryWithDom,
    hasPersistableParams,
    defaultHref,
    restoreVisitOptions = {}
  } = config

  function clearStoredState() {
    safeStorageRemove(sessionStorage, storageKey)
    safeStorageRemove(sessionStorage, restoreInFlightKey)
  }

  function persistSearchString(searchString) {
    const normalized = normalizeStoredSearch(searchString)
    if (normalized === "" || normalized === "?") {
      clearStoredState()
      return
    }

    const params = new URLSearchParams(normalized.replace(/^\?/, ""))
    if (!hasPersistableParams(params)) {
      clearStoredState()
      return
    }

    safeStorageSet(sessionStorage, storageKey, normalized)

    if (safeStorageGet(sessionStorage, restoreInFlightKey) === normalized) {
      safeStorageRemove(sessionStorage, restoreInFlightKey)
    }
  }

  function readStored() {
    return normalizeStoredSearch(safeStorageGet(sessionStorage, storageKey) || "")
  }

  function applyStoredQueryToHref(element) {
    const stored = readStored()
    if (!stored || stored === "" || stored === "?") return

    const currentHref = element.getAttribute("href") || defaultHref
    try {
      const url = new URL(currentHref, window.location.origin)
      if (!isIndexPath(url.pathname)) return

      element.setAttribute("href", `${url.pathname}${stored}${url.hash || ""}`)
    } catch {
      /* ignore invalid href */
    }
  }

  function persistCurrentQueryIfFiltered() {
    if (!isIndexPath(window.location.pathname)) return

    const merged = mergeQueryWithDom(window.location.search || "")
    const params = new URLSearchParams(merged.replace(/^\?/, ""))
    if (!hasPersistableParams(params)) return

    persistSearchString(merged)
  }

  function searchFromResultsFrameLoad(event) {
    const detail = event.detail || {}
    let search = null

    const responseUrl = detail.fetchResponse?.response?.url
    if (responseUrl) {
      try {
        const u = new URL(responseUrl)
        if (isIndexPath(u.pathname)) search = u.search
      } catch {
        /* ignore */
      }
    }

    if (search == null && isIndexPath(window.location.pathname)) {
      search = window.location.search || ""
    }

    if ((search == null || search === "") && event.target?.src) {
      try {
        const u = new URL(event.target.src, window.location.origin)
        if (isIndexPath(u.pathname)) search = u.search || ""
      } catch {
        /* ignore */
      }
    }

    if (search != null) persistSearchString(mergeQueryWithDom(search))
  }

  function maybeRestoreFromStorage() {
    if (!isIndexPath(window.location.pathname)) return

    const stored = readStored()
    if (!stored || stored === "" || stored === "?") return

    const domQuery = normalizeStoredSearch(mergeQueryWithDom(window.location.search || ""))
    if (domQuery === stored) {
      safeStorageRemove(sessionStorage, restoreInFlightKey)
      return
    }

    if (safeStorageGet(sessionStorage, restoreInFlightKey) === stored) return

    const current = new URLSearchParams(window.location.search.replace(/^\?/, ""))
    if (hasPersistableParams(current)) return

    const visitUrl = `${window.location.pathname}${stored}`

    requestAnimationFrame(() => {
      safeStorageSet(sessionStorage, restoreInFlightKey, stored)
      Turbo.visit(visitUrl, { action: "replace", ...restoreVisitOptions })
    })
  }

  return {
    clearStoredState,
    persistSearchString,
    applyStoredQueryToHref,
    persistCurrentQueryIfFiltered,
    searchFromResultsFrameLoad,
    maybeRestoreFromStorage
  }
}

export function defineAdminIndexFiltersStorageController(config) {
  const storage = createAdminIndexFiltersStorage(config)

  return class extends Controller {
    static values = {
      apply: { type: Boolean, default: false }
    }

    connect() {
      if (this.applyValue) {
        storage.applyStoredQueryToHref(this.element)
        return
      }

      this.boundOnResultsFrameLoad = this.onResultsFrameLoad.bind(this)
      this.resultsFrame = document.getElementById("index_results")
      if (this.resultsFrame) {
        this.resultsFrame.addEventListener("turbo:frame-load", this.boundOnResultsFrameLoad)
      }

      storage.maybeRestoreFromStorage()
      storage.persistCurrentQueryIfFiltered()
    }

    disconnect() {
      if (this.resultsFrame) {
        this.resultsFrame.removeEventListener("turbo:frame-load", this.boundOnResultsFrameLoad)
        this.resultsFrame = null
      }
    }

    clearStoredState() {
      storage.clearStoredState()
    }

    onResultsFrameLoad(event) {
      storage.searchFromResultsFrameLoad(event)
    }
  }
}
